import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useI18n } from "../i18n/LanguageContext";
import { useRequestModal } from "../context/RequestModalContext";

const SOURCES = {
  wide: { poster: "/video/hero-poster.jpg", webm: "/video/hero.webm", mp4: "/video/hero.mp4" },
  vertical: { poster: "/video/hero-poster-vertical.jpg", webm: "/video/hero-vertical.webm", mp4: "/video/hero-vertical.mp4" },
};

const isVertical = () => window.matchMedia("(max-width: 767px)").matches;

const shouldPlayVideo = () => {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  const c = navigator.connection;
  if (c && (c.saveData || /2g|slow-2g|3g/.test(c.effectiveType || ""))) return false;
  return true;
};

export const Hero = () => {
  const { t } = useI18n();
  const { openRequest } = useRequestModal();
  const [showVideo, setShowVideo] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [variant, setVariant] = useState("wide");

  useEffect(() => {
    setVariant(isVertical() ? "vertical" : "wide");
    setShowVideo(shouldPlayVideo());
  }, []);

  const media = SOURCES[variant];

  return (
    <section id="top" className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src={media.poster}
          alt=""
          className="absolute inset-0 w-full h-full object-cover scale-105"
          fetchPriority="high"
          data-testid="hero-poster"
        />
        {showVideo && (
          <video
            key={variant}
            className={`absolute inset-0 w-full h-full object-cover scale-105 transition-opacity duration-1000 ${videoReady ? "opacity-100" : "opacity-0"}`}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={media.poster}
            tabIndex={-1}
            onCanPlay={() => setVideoReady(true)}
            data-testid="hero-video"
          >
            <source src={media.webm} type="video/webm" />
            <source src={media.mp4} type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-vm-bg via-vm-bg/60 to-black/40" />
        <div className="absolute inset-0 bg-black/40" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-28 md:pb-32">
        <motion.p
          className="vm-label mb-6"
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
        >
          VELMONTÉ · Anything. Anywhere.
        </motion.p>

        <h1 className="font-serif font-light leading-[0.98] text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
          <motion.span className="block" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }}>
            {t("hero.line1")}
          </motion.span>
          <motion.span className="block text-vm-text2" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.45 }}>
            {t("hero.line2")}
          </motion.span>
        </h1>

        <motion.p
          className="mt-8 max-w-2xl text-base md:text-lg text-vm-text2 font-light leading-relaxed"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.7 }}
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85 }}
        >
          <button onClick={() => openRequest("other")} className="vm-btn-primary px-9 py-4 text-xs tracking-[0.2em] uppercase" data-testid="hero-request-button">
            {t("hero.ctaPrimary")}
          </button>
          <a href="#aviation" className="vm-btn-ghost px-9 py-4 text-xs tracking-[0.2em] uppercase text-center" data-testid="hero-discover-button">
            {t("hero.ctaSecondary")}
          </a>
        </motion.div>

        <motion.p className="mt-10 vm-label" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.1 }}>
          {t("common.availability")}
        </motion.p>
      </div>
    </section>
  );
};
